import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Grid from '@mui/material/Unstable_Grid2';
import { Button, TextField, Alert, Typography } from '@mui/material';
import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useUser from '../../hooks/useUser';
import { useAuth } from '../../context/authContext';
import { validatePassword } from '../../util/validation';
import FunZoneLogo from '../components/Login/FunZoneLogo';
import { getLoginFormGridSx } from '../../util/loginFormGridSx';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const { isLoading, error, successMessage, resetPassword } = useUser();
  const { logout, isLoggedIn } = useAuth();

  const resetToken = searchParams.get('token');
  const resetKey = searchParams.get('key');

  // Auto-logout when accessing password reset page
  useEffect(() => {
    if (isLoggedIn) {
      logout();
    }
  }, [isLoggedIn, logout]);

  useEffect(() => {
    if (!resetToken || !resetKey) {
      setPasswordError(
        'Kein Reset-Token oder Reset-Key gefunden. Bitte fordere einen neuen Token an.'
      );
    }
  }, [resetToken, resetKey]);

  async function handleSubmit(event) {
    event.preventDefault();

    if (!resetToken || !resetKey) {
      return;
    }

    // Validate password complexity and equality
    const validation = validatePassword(newPassword, confirmPassword);
    if (!validation.isValid) {
      setPasswordError(validation.errors.join(', '));
      return;
    }

    setPasswordError('');
    await resetPassword(resetToken, resetKey, newPassword);
  }

  function translateError(err) {
    if (!err) return null;
    // Map known backend error messages to German translations
    const translations = {
      'Invalid or expired reset token.':
        'Ungültiger oder abgelaufener Reset-Token. Ein neuer Token wird dir per E-Mail zugestellt. Bitte führe den Vorgang erneut durch.',
      'Reset token, reset key and new password are required.':
        'Reset-Token, Reset-Key und neues Passwort sind erforderlich.',
      'Invalid reset token.': 'Ungültiger Reset-Token.',
      'Invalid reset token or key.': 'Ungültiger Reset-Token oder Reset-Key.',
      'Password does not meet complexity requirements.':
        'Das Passwort erfüllt nicht die Komplexitätsanforderungen.',
      'Password reset token expired. A new reset email has been sent.':
        'Passwort-Reset-Token abgelaufen. Ein neuer Token wird dir per E-Mail zugestellt. Bitte führe den Vorgang erneut durch.',
    };

    // Exact match first
    if (translations[err]) return translations[err];

    // Handle messages prefixed with an HTTP status code (e.g. "400: Invalid reset token.")
    const cleaned = err.replace(/^\d+:\s*/, '');
    if (translations[cleaned]) return translations[cleaned];

    // Default – return the (cleaned) message so that at least something is displayed
    return cleaned || err;
  }

  function translateSuccess(success) {
    let successMessage = success;
    if (success === 'Password reset successfully.')
      successMessage = 'Passwort wurde erfolgreich zurückgesetzt.';
    return successMessage;
  }

  if (!resetToken || !resetKey) {
    return (
      <Grid
        xs={12}
        container
        rowSpacing={2}
        sx={theme => ({ ...viewRootTextPrimarySx, ...getLoginFormGridSx(theme) })}
        width='95%'
      >
        <Grid xs={12}>
          <FunZoneLogo />
        </Grid>
        <Grid xs={12}>
          <Alert severity='error' sx={{ marginBottom: '20px' }}>
            {passwordError}
          </Alert>
        </Grid>
        <Grid xs={12}>
          <Link to='/request-password-reset' style={{ textDecoration: 'none' }}>
            <Button type='button' sx={{ maxWidth: '200px' }}>
              <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: '5px' }} />
              E-Mail erneut senden
            </Button>
          </Link>
        </Grid>
      </Grid>
    );
  }

  if (successMessage) {
    return (
      <Grid
        xs={12}
        container
        rowSpacing={2}
        sx={theme => ({ ...viewRootTextPrimarySx, ...getLoginFormGridSx(theme) })}
        width='95%'
      >
        <Grid xs={12}>
          <FunZoneLogo />
        </Grid>
        <Grid xs={12}>
          <Alert severity='success' sx={{ marginBottom: '20px' }}>
            {translateSuccess(successMessage)}
          </Alert>
        </Grid>
        <Grid xs={12} sx={{ textAlign: 'center' }}>
          <Link to='/' style={{ textDecoration: 'none' }}>
            <Button type='button' sx={{ maxWidth: '200px' }}>
              <FontAwesomeIcon icon={faArrowRight} style={{ marginRight: '10px' }} />
              Zum Login
            </Button>
          </Link>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid
      xs={12}
      container
      rowSpacing={2}
      sx={theme => ({ ...viewRootTextPrimarySx, ...getLoginFormGridSx(theme) })}
      width='95%'
    >
      <form onSubmit={handleSubmit} style={{ width: '100%' }}>
        {/* LOGO */}
        <Grid xs={12}>
          <FunZoneLogo />
        </Grid>

        {/* TITLE */}
        <Grid xs={12}>
          <Typography variant='h5' component='h2' sx={{ textAlign: 'center', mb: 2.5 }}>
            Neues Passwort setzen
          </Typography>
        </Grid>

        {/* INPUTS */}
        <Grid xs={12}>
          <TextField
            label='Neues Passwort'
            variant='outlined'
            type='password'
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            fullWidth
            required
            helperText='Mindestens 8 Zeichen'
          />
        </Grid>
        <Grid xs={12}>
          <TextField
            label='Passwort bestätigen'
            variant='outlined'
            type='password'
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            fullWidth
            required
            error={newPassword !== confirmPassword && confirmPassword !== ''}
            helperText={
              newPassword !== confirmPassword && confirmPassword !== ''
                ? 'Passwörter stimmen nicht überein'
                : ''
            }
          />
        </Grid>

        {/* ERROR */}
        {(error || passwordError) && (
          <Grid xs={12} sx={{ textAlign: 'center', minHeight: '40px', marginBottom: '20px' }}>
            <Alert severity='error'>{translateError(error) || passwordError}</Alert>
          </Grid>
        )}

        <Grid xs={12} sx={{ marginTop: '20px' }} container>
          {/* BACK BUTTON */}
          <Grid xs={12} sm={6} sx={{ textAlign: 'left' }}>
            <Link to='/request-password-reset' style={{ textDecoration: 'none' }}>
              <Button type='button' sx={{ maxWidth: '200px' }}>
                <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: '5px' }} />
                Zurück
              </Button>
            </Link>
          </Grid>
          {/* SUBMIT BUTTON */}
          <Grid xs={12} sm={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type='submit'
              onClick={handleSubmit}
              disabled={isLoading || !newPassword || !confirmPassword}
              sx={{ maxWidth: '200px' }}
            >
              {isLoading ? 'Wird gespeichert...' : 'Passwort ändern'}
              <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '5px' }} />
            </Button>
          </Grid>
        </Grid>
      </form>
    </Grid>
  );
}

export default ResetPassword;
